import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Facets() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ title: "", intro: "", published: true });

  const load = () => api.get("/admin/facets").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put(`/admin/facets/${edit.key}`, form);
      toast.success("Facet page saved");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = async (row) => {
    try {
      await api.put(`/admin/facets/${row.key}`, { title: row.title, intro: row.intro, published: !row.published });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = rows.filter((r) =>
    (kind === "all" || r.kind === kind) && (!q || r.key.includes(q.toLowerCase()))
  );

  return (
    <div className="space-y-4" data-testid="admin-facets-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="font-heading text-2xl font-bold tracking-tight">SEO Landing Pages</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Auto-generated from product attributes. Pages with fewer than 3 in-stock pieces auto-hide.
          </p>
        </div>
        <Input placeholder="Filter by URL…" value={q} onChange={(e) => setQ(e.target.value)} className="w-56 bg-white" data-testid="facet-search" />
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-40 bg-white" data-testid="facet-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {["metal", "purity", "weight", "style", "wearer", "occasion"].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[11px] font-mono uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
              <th className="p-3">Page</th><th className="p-3">Kind</th><th className="p-3">Products</th>
              <th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((r) => (
              <tr key={r.key} className="border-b border-neutral-100" data-testid={`facet-row-${r.key}`}>
                <td className="p-3">
                  <p className="font-medium">{r.title}{r.edited && <span className="text-[10px] font-mono text-brand ml-2">EDITED</span>}</p>
                  <p className="text-xs font-mono text-neutral-400">/jewellery/{r.key}</p>
                </td>
                <td className="p-3 font-mono text-xs uppercase">{r.kind}</td>
                <td className="p-3 font-mono">{r.count}</td>
                <td className="p-3">
                  <span className={`text-[11px] font-mono uppercase px-2 py-0.5 ${r.visible ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-500"}`} data-testid={`facet-status-${r.key}`}>
                    {r.visible ? "Live" : !r.published ? "Unpublished" : "Thin (<3)"}
                  </span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Switch checked={r.published} onCheckedChange={() => toggle(r)} data-testid={`facet-publish-${r.key}`} />
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(r); setForm({ title: r.title, intro: r.intro, published: r.published }); }} data-testid={`facet-edit-${r.key}`}>Edit</Button>
                  <a href={`/jewellery/${r.key}`} target="_blank" rel="noreferrer" className="inline-block align-middle text-neutral-400 hover:text-brand px-1" data-testid={`facet-view-${r.key}`}><ExternalLink size={14} /></a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="sm:max-w-lg" aria-describedby={undefined} data-testid="facet-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit — /jewellery/{edit?.key}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Meta title / H1</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="facet-title-input" /></div>
            <div>
              <Label>Intro copy</Label>
              <textarea value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} rows={3} className="w-full border border-neutral-200 rounded-sm px-3 py-2 text-sm" data-testid="facet-intro-input" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="facet-published-switch" />
              Published (thin pages with &lt;3 products still auto-hide)
            </label>
          </div>
          <Button onClick={save} className="w-full mt-2 bg-stone-900 hover:bg-brand text-white btn-press" data-testid="facet-save-btn">Save</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
